import { useState, useEffect } from 'react';
import { GraduationCap, BookOpen, Brain, Medal, ChevronRight, Star, Users, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Define a type for valid exam types
type ExamType = 'NEET' | 'JEE';

// Define theme type for type safety
type ExamThemeType = {
  gradient: string;
  secondaryGradient: string;
  buttonGradient: string;
  iconBg: string;
  accentColor: string;
  secondaryBg: string;
  pillBg: string;
};

// Define the theme object with proper typing
const examTheme: Record<ExamType, ExamThemeType> = {
  'NEET': {
    gradient: "linear-gradient(135deg, #0f4c81 0%, #1a8cd8 100%)",
    secondaryGradient: "linear-gradient(to right, #329fff, #8ac4ff)",
    buttonGradient: "linear-gradient(to right, #ff5722, #ff9800)",
    iconBg: "bg-blue-600",
    accentColor: "text-blue-300",
    secondaryBg: "bg-blue-900",
    pillBg: "bg-blue-700 bg-opacity-50"
  },
  'JEE': {
    gradient: "linear-gradient(135deg, #4a235a 0%, #8e44ad 100%)",
    secondaryGradient: "linear-gradient(to right, #9b59b6, #c39bd3)",
    buttonGradient: "linear-gradient(to right, #f39c12, #ffcf4b)",
    iconBg: "bg-purple-600",
    accentColor: "text-purple-300",
    secondaryBg: "bg-purple-900",
    pillBg: "bg-purple-700 bg-opacity-50"
  }
};

export default function KaunBanegaKyaContestLink() {
  const [isHovering, setIsHovering] = useState(false);
  const [selectedExam, setSelectedExam] = useState<ExamType>('NEET');
  const [isMobile, setIsMobile] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
  
  // Check if device is mobile based on screen width
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up event listener
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Format countdown time as mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // Function to handle navigation
  // Inside your component:
  const navigate = useNavigate();

  const navigateToContest = () => {
    navigate('/KaunBanegaGames');
    // navigate(`/KBGames?exam=${selectedExam}`);
  };
    
  return (
    <div className="max-w-full mx-auto p-3 overflow-hidden flex justify-center">
      <div 
        className={`relative rounded-2xl overflow-hidden shadow-2xl transition-all duration-300 transform ${isHovering ? 'scale-102' : ''} ${isMobile ? 'w-full' : 'w-5/6 max-w-2xl'}`}
        style={{
          background: examTheme[selectedExam].gradient,
          boxShadow: isHovering ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)" : "0 15px 30px -10px rgba(0, 0, 0, 0.3)"
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Spotlight effect on hover */}
        <div 
          className={`absolute inset-0 bg-gradient-to-tr from-transparent via-white to-transparent opacity-0 transition-opacity duration-700 ${isHovering ? 'opacity-10' : ''}`}
          style={{
            transform: isHovering ? 'translateX(100%)' : 'translateX(-100%)',
            transition: 'transform 1s ease-in-out, opacity 0.5s ease'
          }}
        ></div>
        
        {/* Animated particles in background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-white opacity-10"
              style={{
                width: `${10 + Math.random() * 20}px`,
                height: `${10 + Math.random() * 20}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${10 + Math.random() * 15}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>
        
        {/* Content container */}
        <div className="relative z-10 p-4 md:p-6">
          {/* Top section with exam selector */}
          <div className="flex flex-col md:flex-row items-center justify-between mb-4">
            <div className="flex items-center gap-3 mb-3 md:mb-0">
              <div className="p-2 rounded-lg bg-white bg-opacity-20 backdrop-filter backdrop-blur-sm shadow-inner">
                <GraduationCap className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                  Kaun Banega Kya
                </h2>
                <p className="text-sm text-white text-opacity-80">Premium Test Series</p>
              </div>
            </div>
            
            <div className="flex bg-black bg-opacity-20 rounded-full p-1">
              <button 
                className={`px-4 py-1 rounded-full text-sm font-medium transition-all ${selectedExam === 'NEET' ? 'bg-blue-600 text-white shadow-lg' : 'text-white text-opacity-80'}`}
                onClick={() => setSelectedExam('NEET')}
              >
                NEET
              </button>
              <button 
                className={`px-4 py-1 rounded-full text-sm font-medium transition-all ${selectedExam === 'JEE' ? 'bg-purple-600 text-white shadow-lg' : 'text-white text-opacity-80'}`}
                onClick={() => setSelectedExam('JEE')}
              >
                JEE
              </button>
            </div>
          </div>
          
          {/* Contest highlights */}
          <div className="bg-black bg-opacity-20 backdrop-filter backdrop-blur-sm rounded-xl p-4 mb-4">
            <div className="flex flex-col md:flex-row gap-4 justify-around">
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 ${examTheme[selectedExam].iconBg}`}>
                  <Medal className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold">Top Rankers</p>
                  <p className={`text-xs ${examTheme[selectedExam].accentColor}`}>Win scholarships</p>
                </div>
              </div>
              
              <div className="hidden md:flex w-px h-12 bg-white bg-opacity-20 self-center"></div>
              
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 ${examTheme[selectedExam].iconBg}`}>
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold">NCERT Based</p>
                  <p className={`text-xs ${examTheme[selectedExam].accentColor}`}>Expert-curated questions</p>
                </div>
              </div>
              
              <div className="hidden md:flex w-px h-12 bg-white bg-opacity-20 self-center"></div>
              
              <div className="flex items-center gap-3">
                <div className={`rounded-full p-2 ${examTheme[selectedExam].iconBg}`}>
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold">All Subjects</p>
                  <p className={`text-xs ${examTheme[selectedExam].accentColor}`}>Complete preparation</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Stats and CTA */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2 md:gap-3">
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs text-white ${examTheme[selectedExam].pillBg}`}>
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                <span>4.9/5 Rating</span>
              </div>
              
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs text-white ${examTheme[selectedExam].pillBg}`}>
                <Users className="w-3 h-3" />
                <span>12K+ Students</span>
              </div>
              
              <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs text-white ${examTheme[selectedExam].pillBg}`}>
                <Clock className="w-3 h-3" />
                <span>Starts in {formatTime(countdown)}</span>
              </div>
            </div>
            
            <button 
              className="w-full md:w-auto px-6 py-3 rounded-lg font-bold text-black shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2"
              style={{ background: examTheme[selectedExam].buttonGradient }}
              onClick={navigateToContest}
            >
              <span>JOIN CONTEST</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          {/* Featured badge */}
          <div className="absolute top-4 -right-10 bg-yellow-400 text-black text-xs font-bold px-12 py-1 transform rotate-45 shadow-md">
            FEATURED
          </div>
          
          {/* Special offer banner */}
          <div className="mt-4 text-center">
            <p className="text-xs text-white text-opacity-80">
              <span className="inline-block animate-pulse bg-red-500 rounded-full w-2 h-2 mr-1"></span>
              Live now: Free mock test for first 500 registrations
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}